import {
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type SortingState,
  type Table,
} from '@tanstack/react-table';
import { useMemo, useState, type ReactNode } from 'react';

import { Button } from '../shadcn/button';
import type { StaticTableProps, TableColumnConfig } from './types';

export function useStaticTable<RowData>({
  data,
  columns,
  pageSize = 5,
  showIndexColumn = true,
  indexColumnTitle = '#',
  actionColumnOption,
}: StaticTableProps<RowData>) {
  const [sorting, setSorting] = useState<SortingState>([]);

  const _columns = useMemo(() => {
    const indexColumn = showIndexColumn ? getRowIndexColumn<RowData>(indexColumnTitle) : null;
    const actionColumn = actionColumnOption
      ? getRowActionColumn<RowData>(actionColumnOption)
      : null;
    return [indexColumn, ...columns, actionColumn].filter(Boolean) as TableColumnConfig<RowData>[];
  }, [columns, showIndexColumn, indexColumnTitle, actionColumnOption]);

  const gridTemplateColumns = useMemo(() => {
    return _columns.map((col) => {
      return col.maxSize != null ? `minmax(${col.size}, ${col.maxSize})` : `${col.size}`;
    });
  }, [_columns]);

  const tableColumns = useMemo(
    () =>
      _columns.map((col) => ({
        accessorKey: col.key,
        accessorFn: col.accessorFn,
        header: typeof col.header === 'string' ? col.header : () => col.header,
        enableSorting: col.enableSorting ?? false,
        cell: col.cell,
        meta: {
          tableCellClassName: col.tableCellClassName,
        },
      })),
    [_columns],
  );

  const table = useReactTable({
    data,
    columns: tableColumns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: {
        pageSize,
      },
    },
    enableColumnResizing: false,
  });

  return {
    table,
    gridTemplateColumns,
  };
}

function getRowIndexColumn<RowData>(indexColumnTitle: ReactNode): TableColumnConfig<RowData> {
  return {
    // `ROW_INDEX_COLUMN` is a special column that is used to display the index of the row.
    // So we need to use `__index__` as the accessor key by forcing the type to `keyof RowData`.
    key: '__index__' as keyof RowData,
    header: indexColumnTitle,
    size: '60px',
    cell: ({ row, table }) => {
      const pageIndex = table.getState().pagination.pageIndex;
      const pageSize = table.getState().pagination.pageSize;
      const rowModel = table.getRowModel();
      const visualIndex = rowModel.rows.findIndex((r) => r.id === row.id);
      const actualIndex = pageIndex * pageSize + visualIndex + 1;
      return <span className="text-base-foreground font-normal">{actualIndex}</span>;
    },
    enableSorting: false,
  };
}

function getRowActionColumn<RowData>(actionColumnOption: {
  buttonText?: string;
  getButtonText?: (row: RowData) => string;
  isDisabled?: (row: RowData) => boolean;
  onClick: (event: React.MouseEvent, row: RowData, table: Table<RowData>) => void;
}): TableColumnConfig<RowData> {
  return {
    key: '__action__' as keyof RowData,
    header: '',
    size: '156px',
    cell: ({ row, table }) => {
      const buttonText = actionColumnOption.getButtonText
        ? actionColumnOption.getButtonText(row.original)
        : actionColumnOption.buttonText || 'Action';

      const isDisabled = actionColumnOption.isDisabled?.(row.original) || false;

      return (
        <Button
          variant="outline"
          size="sm"
          className="text-base-foreground font-normal"
          disabled={isDisabled}
          onClick={(event) => {
            if (!isDisabled) {
              event.stopPropagation();
              actionColumnOption.onClick(event, row.original, table);
            }
          }}
        >
          {buttonText}
        </Button>
      );
    },
  };
}
